import type { Address } from "../../model/Address";

/**
 * This function will take an autocomplete suggestion and, if possible,
 * return a compiled address.
 *
 * @param suggestion google.maps.places.AutocompleteSuggestion
 * @returns
 */
export const getSuggestionAsAddress = async (
  suggestion: google.maps.places.AutocompleteSuggestion,
): Promise<Address | undefined> => {
  try {
    const result = await getSuggestionAsPlace(suggestion);
    if (result) return getPlaceAsAddress(result, suggestion);
  } catch (e) {
    console.error(e);
  }
};

/**
 * This will take an autocomplete suggestion and request the place
 * information from Google Places API.
 * @param suggestion The autocomplete suggestion
 * @returns Promise of place or undefined
 */
export const getSuggestionAsPlace = async (
  suggestion: google.maps.places.AutocompleteSuggestion,
  fields = [
    "displayName",
    "websiteURI",
    "nationalPhoneNumber",
    "addressComponents",
  ],
): Promise<google.maps.places.Place | undefined> => {
  const placeId = suggestion.placePrediction?.placeId;
  if (!placeId) return;

  const place = new google.maps.places.Place({ id: placeId });
  await place.fetchFields({ fields });

  return place;
};

/**
 * This converts a google Place object into a formatted Address object, or undefined if failure
 * @param placeService The place returned from google places API. Must have addressComponents field.
 * @param suggestion (optional) The original autocomplete suggestion, used as a fallback for street number
 * @returns Address
 */
export const getPlaceAsAddress = (
  placeService: google.maps.places.Place,
  suggestion?: google.maps.places.AutocompleteSuggestion,
): Address | undefined => {
  const components = placeService.addressComponents;
  if (!components) return;

  const getComponent = (type: string) =>
    components.find((component) => component.types.includes(type))?.longText ||
    "";

  const getComponentShort = (type: string) =>
    components.find((component) => component.types.includes(type))?.shortText ||
    "";

  const city =
    getComponent("locality") ||
    getComponent("postal_town") ||
    getComponent("sublocality") ||
    getComponent("administrative_area_level_3") ||
    getComponent("neighborhood");
  let streetNum = getComponent("street_number");
  const route = getComponent("route");

  // Sometimes google places won't return a street number.
  // This fallback will extract the street number when possible
  if (!streetNum && suggestion?.placePrediction?.mainText?.text) {
    const match =
      suggestion.placePrediction.mainText.text.match(/^(\d+)\s+(.*)$/);
    if (match) streetNum = match[1];
  }

  if (!route || !streetNum) return;

  return {
    id: "",
    first_line: `${streetNum} ${route}`.trim(),
    second_line: "",
    city,
    state: getComponentShort("administrative_area_level_1"),
    zip_code: +getComponent("postal_code"),
  };
};

/**
 * Requests autocomplete suggestions for the given string
 * from Google Place API.
 *
 * @param input Text input to filter results
 * @param includedPrimaryTypes optional; sets the includedPrimaryTypes for the request. Default is "geocode"
 * @returns google.maps.places.AutocompleteSuggestion
 */
export const getAutocompleteSuggestionFromInput = async (
  input: string,
  includedPrimaryTypes = ["geocode"],
): Promise<google.maps.places.AutocompleteSuggestion[]> => {
  if (!input) return [];
  const { AutocompleteSessionToken, AutocompleteSuggestion } =
    (await google.maps.importLibrary("places")) as google.maps.PlacesLibrary;
  const request: google.maps.places.AutocompleteRequest = {
    input,
    includedPrimaryTypes,
    region: "us",
    language: "en-US",
    sessionToken: new AutocompleteSessionToken(),
  };

  const result = (
    await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
  ).suggestions;

  return result;
};
